import { PromptSelector } from '@startupquickstart/frontend';
import { AiGenerateDialog } from '@/components/ai-job/AiGenerateDialog';
import { Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';

type DescriptionType = 'both' | 'description' | 'long_description';

interface GenerateDescriptionsActionProps {
  attractionId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Dialog for generating attraction descriptions.
 * When generating both short and long, allows selecting a different prompt for each.
 */
export function GenerateDescriptionsAction({
  attractionId,
  open,
  onOpenChange,
}: GenerateDescriptionsActionProps) {
  const [descriptionType, setDescriptionType] = useState<DescriptionType>('both');
  const [shortPromptId, setShortPromptId] = useState<string | undefined>();
  const [longPromptId, setLongPromptId] = useState<string | undefined>();

  useEffect(() => {
    if (open) {
      setDescriptionType('both');
      setShortPromptId(undefined);
      setLongPromptId(undefined);
    }
  }, [open]);

  const buildPayload = () => {
    if (descriptionType === 'both') {
      return { descriptionType, shortPromptId, longPromptId };
    }
    const promptId = descriptionType === 'description' ? shortPromptId : longPromptId;
    return { descriptionType, promptId };
  };

  return (
    <AiGenerateDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Generate Descriptions"
      description={
        <span className="space-y-2 block">
          <span className="block">Generate a description for this attraction.</span>
          <span className="block text-destructive font-medium">
            WARNING: This will overwrite the current descriptions based on the selected type.
          </span>
        </span>
      }
      endpoint={`/attractions/${attractionId}/generate-descriptions`}
      invalidateKeys={[['Attraction', attractionId]]}
      successMessage="Descriptions generated successfully"
      errorFallback="Failed to generate descriptions"
      buildPayload={buildPayload}
    >
      <div>
        <label htmlFor="descriptionType" className="block text-sm font-medium mb-1">
          Description Type <span className="text-destructive">*</span>
        </label>
        <select
          id="descriptionType"
          required
          className="w-full px-3 py-2 border rounded-md"
          value={descriptionType}
          onChange={(e) => setDescriptionType(e.target.value as DescriptionType)}
        >
          <option value="both">Both</option>
          <option value="description">Short Description</option>
          <option value="long_description">Long Description</option>
        </select>
      </div>

      {(descriptionType === 'both' || descriptionType === 'description') && (
        <PromptSelector
          purpose="attraction_short_description"
          value={shortPromptId}
          onChange={setShortPromptId}
          label="Short Description Prompt"
        />
      )}
      {(descriptionType === 'both' || descriptionType === 'long_description') && (
        <PromptSelector
          purpose="attraction_long_description"
          value={longPromptId}
          onChange={setLongPromptId}
          label="Long Description Prompt"
        />
      )}
    </AiGenerateDialog>
  );
}

// Export the icon for use in the custom action
export const GenerateDescriptionsIcon = Sparkles;
